import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterPage, SessionStorageSavedView } from "./PageView.types";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { SavedView } from "../components/savedViews/SavedViewsMenu.types.ts";
import { isNullOrWhitespace } from "../stringOperations.ts";
import { comparableFilterData, nextStoredFilterData, pinnedFilterIdsKey } from "../components/filterBar/filterPinning.ts";
import { filterOperatorsKey } from "../components/filterBar/filterOperatorHydration.ts";

function buildStorageKey(customerId: string, page: FilterPage, scopeKey: string | undefined): string {
    const base = `pv:${customerId}:${FilterPage[page]}`;
    return scopeKey ? `${base}:${scopeKey}` : base;
}

function safeParse<T>(json: string | null): T | undefined {
    if (!json) return undefined;
    try {
        return JSON.parse(json) as T;
    } catch {
        return undefined;
    }
}

function readStoredView(customerId: string, page: FilterPage, scopeKey: string | undefined): SessionStorageSavedView | undefined {
    const key = buildStorageKey(customerId, page, scopeKey);
    const data = safeParse<SessionStorageSavedView>(sessionStorage.getItem(key));

    if (!data) return undefined;

    // Rename counterpartyIds -> newPropertyName in filterData
    if (data.filterData?.counterpartyIds) {
        data.filterData.externalContractPartyIds = data.filterData.counterpartyIds;
        delete data.filterData.counterpartyIds;
    }

    // Rename 'counterparty-ids' -> 'new-property-name' in displayData
    if (Array.isArray(data.displayData)) {
        const index = data.displayData.indexOf("counterparty-ids");
        if (index !== -1) {
            data.displayData[index] = "external-contractParty-ids";
        }
    }

    return data;
}

function writeStoredView(customerId: string, page: FilterPage, scopeKey: string | undefined, view: SessionStorageSavedView | undefined): void {
    const key = buildStorageKey(customerId, page, scopeKey);
    if (view === undefined) {
        sessionStorage.removeItem(key);
        return;
    }
    sessionStorage.setItem(key, JSON.stringify(view));
}

// The pinned ids and the filter operators ride inside the filter blob so they travel with a saved view, but every
// entity's transformXFilterOptionsToBackend builds a fresh request object and knows nothing about them - without this
// they would be dropped on the next filter change.
function carryFilterPresentationState<T>(next: T | undefined, previous: unknown): T | undefined {
    if (next === undefined || next === null) {
        return next;
    }

    // previous is SessionStorageSavedView.filterData, which is typed any, so the shape has to be asserted here.
    const source = previous as Record<string, unknown> | undefined;
    let carried = next;

    const pinned = source?.[pinnedFilterIdsKey];
    if (pinned) {
        carried = { ...carried, [pinnedFilterIdsKey]: pinned };
    }

    // Only when the incoming blob has none of its own. A filter-bar change writes the whole map, and the previous one
    // is stale by definition; this is for the writers that build a request object from something other than the bar.
    const operators = source?.[filterOperatorsKey];
    if (operators && !(filterOperatorsKey in (carried as object))) {
        carried = { ...carried, [filterOperatorsKey]: operators };
    }

    return carried;
}

// toViewFilterBaseline lets a page carry part of the current filter over onto the view before it is compared or cleared
// back to, for a filter that picks what the page shows rather than narrowing it.
export function usePageView<TFilter = unknown, TDisplay = unknown>(
    page: FilterPage,
    savedViews: Array<SavedView> | undefined,
    scopeKey?: string,
    toViewFilterBaseline?: (viewFilterData: TFilter, currentFilterData: TFilter | undefined) => TFilter
) {
    const { customerId } = useUserContext();

    // Read initial value from storage (do not create defaults if missing)
    const initial = useMemo(() => readStoredView(customerId, page, scopeKey), [customerId, page, scopeKey]);
    const [view, setViewState] = useState<SessionStorageSavedView | undefined>(initial);

    useEffect(() => {
        if (view === undefined) {
            resetToDefaultView();
        }
        // eslint-disable-next-line
    }, [savedViews]);

    const setView = useCallback(
        (updatedView: SessionStorageSavedView) => {
            setViewState(updatedView);
            writeStoredView(customerId, page, scopeKey, updatedView);
        },
        [customerId, page, scopeKey]
    );

    const resetToDefaultView = useCallback(() => {
        const defaultView = (savedViews ?? []).find((sv) => sv.isUserDefault);
        if (defaultView) {
            setView({
                page,
                viewId: defaultView.id,
                filterData: defaultView.filterData,
                displayData: defaultView.displayData,
            });
        }
    }, [savedViews, page, setView]);

    const setFilterData = useCallback(
        (data: TFilter | undefined) => {
            // Read back from storage rather than the render's `view`, so a display update written in the same event is kept.
            const stored = readStoredView(customerId, page, scopeKey);
            let updated: SessionStorageSavedView | undefined;
            if (data === undefined && stored === undefined) {
                updated = undefined;
            } else {
                updated = {
                    page,
                    viewId: stored?.viewId,
                    filterData: carryFilterPresentationState(data, stored?.filterData),
                    displayData: stored?.displayData,
                };
            }

            setViewState(updated);
            writeStoredView(customerId, page, scopeKey, updated);
        },
        [customerId, page, scopeKey]
    );

    const setPinnedFilterIds = useCallback(
        (ids: Array<string> | undefined) => {
            // Read back from storage rather than the render's `view`: the page writes its own filter update in the
            // same event, and this would otherwise persist a snapshot taken before that write.
            const stored = readStoredView(customerId, page, scopeKey);

            // Seed from the same fallback getFilterData uses, so pinning on a page that has not written yet
            // carries the user's default view forward instead of replacing it with a pins-only blob. The whole
            // view is seeded, not just its filters, so the write matches what resetToDefaultView would have stored.
            const defaultView = stored === undefined ? (savedViews ?? []).find((sv) => sv.isUserDefault) : undefined;
            const filterData = nextStoredFilterData(stored?.filterData ?? defaultView?.filterData, ids);
            if (filterData === undefined) {
                return;
            }

            const updated: SessionStorageSavedView = {
                page,
                // Not ??: the system default's id is null, and ?? would swap it for undefined, which drops the view.
                viewId: stored ? stored.viewId : defaultView?.id,
                filterData,
                displayData: stored?.displayData ?? defaultView?.displayData,
            };

            setViewState(updated);
            writeStoredView(customerId, page, scopeKey, updated);
        },
        [customerId, page, scopeKey, savedViews]
    );

    const setDisplayData = useCallback(
        (data: TDisplay | undefined) => {
            // Read back from storage rather than the render's `view`, so a filter update written in the same event is kept,
            // as when a deep link seeds the filter and then adds the columns it sorts by.
            const stored = readStoredView(customerId, page, scopeKey);
            let updated: SessionStorageSavedView | undefined;
            if (data === undefined && stored === undefined) {
                updated = undefined;
            } else {
                updated = {
                    page,
                    viewId: stored?.viewId,
                    filterData: stored?.filterData,
                    displayData: data,
                };
            }

            setViewState(updated);
            writeStoredView(customerId, page, scopeKey, updated);
        },
        [customerId, page, scopeKey]
    );

    // The view a stored filter is measured against. The system default is served with a null id, while a stored view
    // that was never pointed at a real view carries an undefined one, so the ids have to be normalised before they can
    // match. A viewId that resolves to nothing at all - a deleted view, or a blob written before the current view set
    // loaded - falls back to the system default: without it the lookup misses, every comparison below reports
    // "unchanged" no matter what the user filtered by, and the page believes no filter is active.
    function getComparisonView(): SavedView | undefined {
        const views = savedViews ?? [];
        return views.find((sv) => sv.id === (view?.viewId ?? null)) ?? views.find((sv) => sv.isSystemDefault);
    }

    function getViewFilterBaseline(savedView: SavedView) {
        return toViewFilterBaseline ? toViewFilterBaseline(savedView.filterData, view?.filterData) : savedView.filterData;
    }

    function clearFilterData() {
        const savedView = getComparisonView();
        if (savedView) {
            // Reset to the saved view's default filter state
            const resetView: SessionStorageSavedView = {
                page,
                viewId: savedView.id,
                filterData: getViewFilterBaseline(savedView),
                displayData: view?.displayData, // Preserve current display data
            };
            setViewState(resetView);
            writeStoredView(customerId, page, scopeKey, resetView);
            return;
        }
    }

    // Change detection using the generic comparison functions
    function hasFilterChanged(): boolean {
        if (view) {
            const savedView = getComparisonView();
            if (savedView) {
                // Reset to the saved view's default filter state
                return hasDataChanged(view?.filterData, getViewFilterBaseline(savedView));
            }
        }

        return false;
    }

    function hasDisplayChanged(): boolean {
        if (view) {
            const savedView = getComparisonView();

            if (savedView) {
                return JSON.stringify(view.displayData ?? []) !== JSON.stringify(savedView.displayData ?? []);
            }
        }

        return false;
    }

    function getFilterData<T>(): T | undefined {
        if (!customerId) return undefined;

        const stored = readStoredView(customerId, page, scopeKey);
        if (stored?.filterData === undefined) {
            const savedView = (savedViews ?? []).find((sv) => sv.isUserDefault);
            if (savedView) {
                // Reset to the saved view's default filter state
                return savedView.filterData as T | undefined;
            }
        }

        return stored?.filterData as T | undefined;
    }

    function getDisplayData<U>(): U | undefined {
        if (!customerId) return undefined;

        const stored = readStoredView(customerId, page, scopeKey);
        if (stored?.displayData === undefined) {
            const savedView = (savedViews ?? []).find((sv) => sv.isUserDefault);
            if (savedView) {
                // Reset to the saved view's default filter state
                return savedView.displayData as U | undefined;
            }
        }

        return stored?.displayData as U | undefined;
    }

    function getPinnedFilterIds(): Array<string> {
        return getFilterData<{ pinnedFilterIds?: Array<string> }>()?.pinnedFilterIds ?? [];
    }

    function getSavedView() {
        // Id from systemDefault is null. Undefined wont find the id!
        let idToFind = view?.viewId ?? null;
        return (savedViews ?? []).find((sv) => sv.id === idToFind);
    }

    // The table keeps sort and page in its own sessionStorage slot. The key carries the view id so applying another
    // view starts the table over from that view's sort instead of restoring whatever the previous view showed.
    const tableSessionStateKey = `${buildStorageKey(customerId, page, scopeKey)}:${view?.viewId ?? "system-default"}`;

    return {
        currentViewId: view?.viewId,
        tableSessionStateKey,
        setView,
        resetToDefaultView,
        setFilterData,
        setDisplayData,
        clearFilterData,
        getFilterData,
        getDisplayData,
        pinnedFilterIds: getPinnedFilterIds(),
        setPinnedFilterIds,
        hasFilterChanged: hasFilterChanged(),
        hasDisplayChanged: hasDisplayChanged(),
        isSavedView: !isNullOrWhitespace(view?.viewId),
        savedView: getSavedView(),
    };
}

const excludedCompareFields = ["pageNumber", "pageSize", "sortByColumn", "sortDirection", "sortByCustomFieldId"];

// Generic comparison utilities
function hasAnyValues(data: any): boolean {
    if (data === null || data === undefined) return false;
    if (typeof data === "string") return data.length > 0;
    if (typeof data === "number") return true;
    if (typeof data === "boolean") return true;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === "object") {
        return Object.keys(data).some((key) => {
            if (excludedCompareFields.includes(key)) return false;
            return hasAnyValues(data[key]);
        });
    }
    return false;
}

function deepEquals(a: any, b: any): boolean {
    if (a === b) return true;
    if (a === null || b === null || a === undefined || b === undefined) return a === b;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) return false;

        // For arrays, check if they contain the same elements regardless of order
        // For primitive arrays (strings, numbers)
        if (a.length > 0 && (typeof a[0] === "string" || typeof a[0] === "number" || typeof a[0] === "boolean")) {
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            return sortedA.every((item, index) => item === sortedB[index]);
        }
        // For object arrays, check if all items in a exist in b and vice versa
        return a.every((itemA) => b.some((itemB) => deepEquals(itemA, itemB))) && b.every((itemB) => a.some((itemA) => deepEquals(itemA, itemB)));
    }

    if (typeof a === "object") {
        // Exclude pagination fields from comparison

        // Create filtered objects without pagination fields
        const filteredA = Object.fromEntries(
            Object.keys(a)
                .filter((key) => !excludedCompareFields.includes(key))
                .map((key) => [key, a[key]])
        );

        const filteredB = Object.fromEntries(
            Object.keys(b)
                .filter((key) => !excludedCompareFields.includes(key))
                .map((key) => [key, b[key]])
        );

        // Compare the filtered objects
        const keysA = Object.keys(filteredA);
        const keysB = Object.keys(filteredB);
        if (keysA.length !== keysB.length) return false;

        return keysA.every((key) => {
            // Both objects should have the same keys due to identical filtering
            return deepEquals(filteredA[key], filteredB[key]);
        });
    }

    return false;
}

export function hasDataChanged<T>(storedData: any, model: T | null | undefined): boolean {
    // Both sides lose their presentation state and their valueless filters first, so a filter the user emptied and one
    // they never added compare the same. See comparableFilterData.
    const storedFilters = comparableFilterData(storedData);

    // If no model provided, check if stored data has any meaningful values
    if (model === null || model === undefined) {
        return hasAnyValues(storedFilters);
    }

    // If model provided, do deep comparison
    return !deepEquals(storedFilters, comparableFilterData(model));
}
